// AI-assisted: Claude 2026-08-07, human-reviewed: ausstehend
// Intervall-Timer-Logik (zeitstempel-basiert → korrekt nach Handy-Sperre) +
// Audio/Vibration. unlockAudio/playBeep wörtlich aus kraftbogen/frontend/src/lib/timer.ts.
import type { TimerConfig } from "./types"

export interface IntervalPhase {
  kind: "work" | "rest"
  round: number        // 1-basiert
  totalRounds: number
  durationSec: number
}

/** Arbeit/Pause-Phasen aus der Konfiguration; nach der letzten Runde keine Pause. */
export function buildPhases(cfg: TimerConfig): IntervalPhase[] {
  const rounds = Math.max(1, Math.floor(cfg.rounds))
  const work = Math.max(1, Math.floor(cfg.work_sec))
  const rest = Math.max(0, Math.floor(cfg.rest_sec))
  const phases: IntervalPhase[] = []
  for (let r = 1; r <= rounds; r++) {
    phases.push({ kind: "work", round: r, totalRounds: rounds, durationSec: work })
    if (rest > 0 && r < rounds) phases.push({ kind: "rest", round: r, totalRounds: rounds, durationSec: rest })
  }
  return phases
}

export interface IntervalState {
  done: boolean
  phaseIndex: number
  phase: IntervalPhase | null
  remainingSec: number     // in der aktuellen Phase, aufgerundet
  countdown: boolean       // letzte 3 Sekunden der Phase
}

/** Zustand bei `elapsedSec` seit Start — reine Funktion, sprungfest (Handy-Sperre). */
export function stateAt(phases: IntervalPhase[], elapsedSec: number): IntervalState {
  let t = Math.max(0, elapsedSec)
  for (let i = 0; i < phases.length; i++) {
    const p = phases[i]
    if (t < p.durationSec) {
      const remaining = Math.ceil(p.durationSec - t)
      return { done: false, phaseIndex: i, phase: p, remainingSec: remaining, countdown: remaining <= 3 }
    }
    t -= p.durationSec
  }
  return { done: true, phaseIndex: phases.length, phase: null, remainingSec: 0, countdown: false }
}

export function totalDuration(phases: IntervalPhase[]): number {
  return phases.reduce((s, p) => s + p.durationSec, 0)
}

// ── Audio (Web Audio, iOS-PWA-sicher) — Kopie aus Kraftbogen ──

let ctx: AudioContext | null = null

function audioCtor(): typeof AudioContext | undefined {
  if (typeof window === "undefined") return undefined
  const w = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }
  return w.AudioContext ?? w.webkitAudioContext
}

/** Beim ersten Nutzer-Tipp aufrufen (Timer-Start) — schaltet Audio auf iOS frei. */
export function unlockAudio(): void {
  const Ctor = audioCtor()
  if (!Ctor) return
  try {
    if (!ctx) ctx = new Ctor()
    if (ctx.state === "suspended") void ctx.resume()
  } catch {
    ctx = null
  }
}

/** Kurzer Piep. Kein Fehler, wenn Audio nicht verfügbar ist. */
export function playBeep(durationMs = 180, freq = 880): void {
  const Ctor = audioCtor()
  if (!Ctor) return
  try {
    if (!ctx) ctx = new Ctor()
    if (ctx.state === "suspended") void ctx.resume()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = "sine"
    osc.frequency.value = freq
    gain.gain.value = 0.0001
    osc.connect(gain).connect(ctx.destination)
    const t = ctx.currentTime
    gain.gain.exponentialRampToValueAtTime(0.2, t + 0.01)
    gain.gain.exponentialRampToValueAtTime(0.0001, t + durationMs / 1000)
    osc.start(t)
    osc.stop(t + durationMs / 1000)
  } catch {
    /* still — visuelles Ende reicht */
  }
}

/** Vibration, wo unterstützt (Android/einige Browser). iOS ignoriert das stumm. */
// Schalter aus den Optionen — Ton-Schlüssel teilt sich der Timer-eigene Knopf
export const SOUND_KEY = "fithub:timer-sound"
const VIBRATION_KEY = "fithub:vibration"

export function tonAn(): boolean {
  try { return localStorage.getItem(SOUND_KEY) !== "aus" } catch { return true }
}
export function setTonAn(an: boolean): void {
  try { localStorage.setItem(SOUND_KEY, an ? "an" : "aus") } catch { /* egal */ }
}
export function vibrationAn(): boolean {
  try { return localStorage.getItem(VIBRATION_KEY) !== "aus" } catch { return true }
}
export function setVibrationAn(an: boolean): void {
  try { localStorage.setItem(VIBRATION_KEY, an ? "an" : "aus") } catch { /* egal */ }
}

export function vibrate(pattern: number | number[] = 80): void {
  if (!vibrationAn()) return
  try {
    navigator.vibrate?.(pattern)
  } catch {
    /* nicht verfügbar */
  }
}

/** Bildschirm anlassen während des Timers (Wake-Lock-API, wo verfügbar). */
export async function requestWakeLock(): Promise<{ release: () => void }> {
  try {
    const nav = navigator as Navigator & { wakeLock?: { request: (t: "screen") => Promise<{ release: () => Promise<void> }> } }
    const lock = await nav.wakeLock?.request("screen")
    return { release: () => { void lock?.release() } }
  } catch {
    return { release: () => {} }
  }
}
